import { Slot as SlotPrimitive, Tooltip as TooltipPrimitive } from 'radix-ui';
import type * as React from 'react';
import { cn } from '@/lib/utils';

// shadcn/ui (AD #39): shadcn's Tooltip on Radix, adapted to the CRM's rules.
//   - It looks like design-system §5's tooltip, as `src/ui`'s Tooltip draws it: the card's fill with its
//     border, the menu shadow and `type/meta` text, set off from its trigger by `space/2`, with no arrow
//     (shadcn's is a dark bubble with an arrow).
//   - It opens after the same 400ms as `src/ui`'s Tooltip (`TOOLTIP_DELAY_MS`), stated here because
//     importing that module would load react-aria into every chunk that shows this one.
//   - It sits on the app's menu layer (`--et-z-menu`), not shadcn's `z-50`, above sticky cells.
//   - A tooltip repeats its trigger's accessible name; it never carries the only name a control has.
const TOOLTIP_DELAY_MS = 400;

function TooltipProvider({
    delayDuration = TOOLTIP_DELAY_MS,
    ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
    return (
        <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />
    );
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
    return (
        <TooltipProvider>
            <TooltipPrimitive.Root data-slot="tooltip" {...props} />
        </TooltipProvider>
    );
}

/**
 * The tooltip's trigger. Two adaptations of Radix's, both as `src/ui`'s Tooltip behaves:
 *   - Focus opens the tooltip only when the browser shows keyboard focus (`:focus-visible`). Radix opens it
 *     on any focus not started by a press on the trigger, so focus moved back by script after a mouse
 *     action (a drawer closed by its Close button) popped it up unasked. A handler that cancels the event
 *     stops Radix's own.
 *   - `describesTrigger={false}` is for a control whose name is the tooltip's text, an icon-only button
 *     above all: Radix points the trigger's `aria-describedby` at the open tooltip, which would read the
 *     name a second time. Radix's Slot joins a child's `aria-describedby` with its own rather than letting
 *     either win, so the child sits inside `WithoutDescription`, which drops it and passes every other
 *     prop on through a Slot of its own (handlers composed, ref kept). It needs `asChild`.
 */
function TooltipTrigger({
    describesTrigger = true,
    asChild,
    children,
    onFocus,
    ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger> & { readonly describesTrigger?: boolean }) {
    return (
        <TooltipPrimitive.Trigger
            data-slot="tooltip-trigger"
            asChild={asChild ?? false}
            onFocus={(event) => {
                onFocus?.(event);
                if (!focusIsVisible(event.currentTarget)) {
                    event.preventDefault();
                }
            }}
            {...props}
        >
            {asChild && !describesTrigger ? <WithoutDescription>{children}</WithoutDescription> : children}
        </TooltipPrimitive.Trigger>
    );
}

/** A Slot that passes on every prop but `aria-describedby` (`TooltipTrigger`'s `describesTrigger`). */
function WithoutDescription({
    'aria-describedby': _description,
    ...props
}: React.ComponentProps<typeof SlotPrimitive.Root>) {
    return <SlotPrimitive.Root {...props} />;
}

/** Whether `element`'s focus is drawn as keyboard focus; true where the selector is not supported. */
function focusIsVisible(element: Element): boolean {
    try {
        return element.matches(':focus-visible');
    } catch {
        return true;
    }
}

function TooltipContent({
    className,
    children,
    ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
    return (
        <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
                data-slot="tooltip-content"
                className={cn(
                    'z-(--et-z-menu) w-fit max-w-(--et-layout-tooltip-max) origin-(--radix-tooltip-content-transform-origin) rounded-(--et-radius-md) border [border-color:var(--et-border-card)] [background-color:var(--et-surface-card)] px-(--et-space-4) py-(--et-space-3) [font-family:var(--et-type-meta-face)] text-[length:var(--et-type-meta-size)] leading-(--et-type-meta-line) [font-weight:var(--et-type-meta-weight)] tracking-(--et-type-meta-tracking) [color:var(--et-text-primary)] [box-shadow:var(--et-shadow-menu)] animate-in fade-in-0 data-[side=bottom]:mt-(--et-space-2) data-[side=left]:mr-(--et-space-2) data-[side=right]:ml-(--et-space-2) data-[side=top]:mb-(--et-space-2) data-[state=closed]:animate-out data-[state=closed]:fade-out-0 motion-reduce:animate-none',
                    className,
                )}
                {...props}
            >
                {children}
            </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
    );
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
